<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import BuildingsList from './components/BuildingsList.vue';
import { useBuildingStore, usePeopleTypesStore, PersonStores } from '@/store';

type BuildingType = {
  id: number;
  building_id?: number;
  name: string;
};

type CreateBuildingData = {
  name: string;
  attachClinicians: number[];
  attached: number[];
  detached: number[];
};

type EditBuildingData = {
  name: string;
  building_id: number;
  attachClinicians: number[];
  detachClinicians: number[];
};

const props = defineProps<{
  organizationId: number;
}>();

const buildingStore = useBuildingStore();
const peopleTypeStore = usePeopleTypesStore();
const profileStore = PersonStores.useUserProfileStore();
const isAdminUser = ref(false);
const ADMIN_TYPES = [
  peopleTypeStore.PEOPLE_TYPES.SUPER_ADMIN,
  peopleTypeStore.PEOPLE_TYPES.SUPPORT_ADMIN,
  peopleTypeStore.PEOPLE_TYPES.BILLING_ADMIN,
  peopleTypeStore.PEOPLE_TYPES.SCHOOL_ADMIN,
];
const buildings = ref<BuildingType[]>([]);
const buildingsOrganization = ref<BuildingType[]>([]);
const totalRecords = ref(0);

onMounted(async () => {
  await init();
});

const fetchBuildings = async (): Promise<void> => {
  await buildingStore.fetchOrganizationBuildings(props.organizationId);
  totalRecords.value = buildingStore.totalBuildingsRecords;

  if (!isAdminUser.value && totalRecords.value > 0) {
    await buildingStore.fetchPersonBuildings(profileStore.person?.id);

    const orgBuildingIds = new Set((buildingStore.organizationBuildings || []).map((b) => b.organization_id));

    buildings.value = (buildingStore.personBuildings || []).filter((b) => orgBuildingIds.has(b.organization_id));
    totalRecords.value = buildings.value.length;
  } else {
    buildings.value = buildingStore.organizationBuildings || [];
    totalRecords.value = buildingStore.totalBuildingsRecords;
  }
};

const init = async (): Promise<void> => {
  await profileStore.getAuthUserProfile();
  const acronym = profileStore.person?.type?.acronym ?? '';
  isAdminUser.value = ADMIN_TYPES.includes(acronym);
  await fetchBuildings();
};

const deleteBuilding = async (building: BuildingType): Promise<void> => {
  if (isAdminUser.value) {
    await buildingStore.deleteOrganizationBuilding(building.id, props.organizationId);
  } else if (building.building_id !== undefined) {
    await buildingStore.detachPersonBuilding({ building_ids: [building.building_id] }, profileStore.person?.id);
  } else {
    buildingStore.doToast('Building ID is missing', '', true);
    return;
  }
  buildingStore.doToast('Building deleted successfully');
  await fetchBuildings();
};

const createBuilding = async (data: CreateBuildingData): Promise<void> => {
  if (isAdminUser.value) {
    const createdBuilding = await buildingStore.createOrganizationBuilding({
      organization_id: props.organizationId,
      name: data.name,
    });

    if (createdBuilding?.id) {
      buildingStore.doToast('Building created successfully');
      await Promise.all(
        data.attachClinicians.map((clinician: number) =>
          buildingStore.attachPersonBuilding({ building_ids: [createdBuilding.id] }, clinician),
        ),
      );
    } else {
      buildingStore.doToast('The name has already been taken.', '', true);
    }
  } else {
    if (!data.attached.length && !data.detached.length) {
      return;
    }
    if (data.detached.length) {
      await buildingStore.detachPersonBuilding({ building_ids: data.detached }, profileStore.person?.id);
    }
    if (data.attached.length) {
      await buildingStore.attachPersonBuilding({ building_ids: data.attached }, profileStore.person?.id);
    }
    buildingStore.doToast('Buildings assigned successfully');
  }
  await fetchBuildings();
};

const editBuilding = async (data: EditBuildingData): Promise<void> => {
  await buildingStore.updateOrganizationBuilding(
    {
      organization_id: props.organizationId,
      name: data.name,
    },
    data.building_id,
  );

  buildingStore.doToast('Building updated successfully');
  await Promise.all(
    data.detachClinicians.map((clinician: number) =>
      buildingStore.detachPersonBuilding({ building_ids: [data.building_id] }, clinician),
    ),
  );

  await Promise.all(
    data.attachClinicians.map((clinician: number) =>
      buildingStore.attachPersonBuilding({ building_ids: [data.building_id] }, clinician),
    ),
  );
  await fetchBuildings();
};

const syncBuildings = async (): Promise<void> => {
  await buildingStore.fetchOrganizationBuildings(props.organizationId);
  buildingsOrganization.value = buildingStore.organizationBuildings;
};
</script>

<template>
  <BuildingsList
    :organization-id="props.organizationId"
    :is-admin-user="isAdminUser"
    :total-records="totalRecords"
    :buildings="buildings"
    :buildings-organization="buildingsOrganization"
    :loading="buildingStore.loading"
    @remove="deleteBuilding"
    @save="createBuilding"
    @edit="editBuilding"
    @sync="syncBuildings"
  />
</template>
